import { useEffect, useMemo, useState } from 'react'
import { Button } from '@highlight/ui'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { FadingScrollable } from '@renderer/chat'
import { useUser } from '@renderer/hooks/useUser'
import {
  bookingWeeks,
  dayKeyOf,
  formatDay,
  formatDayHeading,
  formatDayOfMonth,
  formatMonth,
  formatSlot,
  formatTime,
  slotsByDay,
  timeZoneLabel,
  type DayKey,
} from '../calendar'
import { trackNotchAction } from '../../track-notch'
import { useBookSetupCall, useSetupCallAvailability, type SetupCallBooking } from '../setup-call-api'
import {
  SetupCallRow,
  chosenClassName,
  chosenSubtleTextClassName,
  letGoClassName,
  tactileClassName,
  titleClassName,
} from './face'

const SLOT_COLUMNS = 3
const SLOT_HEIGHT_PX = 28
const SLOT_GAP_PX = 6
/** Past this many rows a day scrolls, so a host spread across zones can't stretch the notch down the screen. */
const SLOT_MAX_ROWS = 6

const slotsHeight = (rows: number) => rows * SLOT_HEIGHT_PX + (rows - 1) * SLOT_GAP_PX

interface SetupCallPickerProps {
  zone: string
  /** Rescheduling: the picker opens on the booked day. */
  booking: SetupCallBooking | null
  onChangeZone: () => void
  onBooked: (booking: SetupCallBooking) => void
  onCancel: () => void
}

// A day and a time are targets with a ground: the subtle surface at rest, the element grey under the pointer
const pickClassName =
  'bg-background-subtle text-foreground flex items-center justify-center corner-squircle transition-colors hover:bg-element disabled:cursor-default disabled:hover:bg-background-subtle'

export function SetupCallPicker({ zone, booking, onChangeZone, onBooked, onCancel }: SetupCallPickerProps) {
  const availability = useSetupCallAvailability(true)
  const book = useBookSetupCall()
  const user = useUser()
  const bookingAs = [[user?.firstName, user?.lastName].filter(Boolean).join(' '), user?.email].filter(Boolean)

  const weeks = useMemo(
    () => (availability.data ? bookingWeeks(availability.data.window.start, zone) : []),
    [availability.data, zone],
  )
  const byDay = useMemo(() => slotsByDay(availability.data?.slots ?? [], zone), [availability.data, zone])
  const firstOpenDay = weeks.flat().find((day) => byDay.has(day))

  const [chosenWeek, setWeek] = useState<number | null>(null)
  const [chosenDay, setDay] = useState<DayKey | null>(null)
  const [slot, setSlot] = useState<string | null>(null)

  const bookedDay = booking ? dayKeyOf(booking.start, zone) : null
  // The booked day even when its only time was the one held, which availability no longer lists
  const day = chosenDay ?? (bookedDay && weeks.flat().includes(bookedDay) ? bookedDay : firstOpenDay) ?? weeks[0]?.[0]
  const week =
    chosenWeek ??
    Math.max(
      0,
      weeks.findIndex((w) => day !== undefined && w.includes(day)),
    )
  const daySlots = (day ? byDay.get(day) : undefined) ?? []
  // Held at the busiest day's height, so moving along the week never resizes the notch under the pointer
  const busiestRows = Math.ceil(Math.max(1, ...weeks.flat().map((d) => byDay.get(d)?.length ?? 0)) / SLOT_COLUMNS)
  const slotsMinHeight = slotsHeight(Math.min(busiestRows, SLOT_MAX_ROWS))
  const nextOpenDay = weeks[week]?.find((d) => d > (day ?? '') && byDay.has(d))

  const confirm = () => {
    if (!slot) return
    book.mutate(
      { start: slot, timezone: zone },
      {
        onSuccess: onBooked,
        onError: (error) =>
          trackNotchAction('setup call', { step: 'booking failed', error: error.message, isReschedule: !!booking }),
      },
    )
  }

  const unavailableReason = availability.isError
    ? 'error'
    : availability.isSuccess && byDay.size === 0
      ? 'none open'
      : null
  useEffect(() => {
    if (unavailableReason) trackNotchAction('setup call', { step: 'no times shown', reason: unavailableReason })
  }, [unavailableReason])

  const chooseWeek = (next: number) => {
    setWeek(next)
    setDay(weeks[next].find((d) => byDay.has(d)) ?? weeks[next][0])
    setSlot(null)
  }

  if (availability.isLoading) {
    return <p className="text-foreground-secondary py-6 text-center text-sm">Finding open times…</p>
  }
  if (availability.isError || weeks.length === 0 || byDay.size === 0) {
    return (
      <div className="flex flex-col">
        <SetupCallRow>
          <p className="text-foreground-secondary text-sm">
            {availability.isError ? 'Couldn’t load open times.' : 'No times are open right now. Try again tomorrow.'}
          </p>
        </SetupCallRow>
        <SetupCallRow className="mt-4 flex gap-2">
          {availability.isError && (
            <Button size="lg" className={tactileClassName} onClick={() => availability.refetch()}>
              Try again
            </Button>
          )}
          <Button variant="ghost" tone="on-dark" size="lg" className={letGoClassName} onClick={onCancel}>
            Close
          </Button>
        </SetupCallRow>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <SetupCallRow className="flex items-center justify-between gap-2">
        <h2 id="setup-call-book-title" className={titleClassName}>
          {day && formatMonth(day)}
        </h2>
        <span className="-m-1 flex gap-0.5">
          <Button
            variant="ghost"
            tone="on-dark"
            size="sm"
            shape="square"
            aria-label="Previous week"
            className="text-foreground-secondary"
            disabled={week === 0}
            onClick={() => chooseWeek(0)}
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="ghost"
            tone="on-dark"
            size="sm"
            shape="square"
            aria-label="Next week"
            className="text-foreground-secondary"
            disabled={week === 1}
            onClick={() => chooseWeek(1)}
          >
            <ChevronRight />
          </Button>
        </span>
      </SetupCallRow>

      <SetupCallRow className="mt-2 grid grid-cols-5 gap-1" role="group" aria-label="Day">
        {weeks[week].map((key) => {
          const isChosen = key === day
          const isFull = !byDay.has(key)
          return (
            <button
              key={key}
              type="button"
              aria-pressed={isChosen}
              aria-label={`${formatDay(key, 'long')} ${formatDayOfMonth(key)}${isFull ? ', full' : ''}`}
              disabled={isFull}
              onClick={() => {
                setDay(key)
                setSlot(null)
              }}
              className={twMerge(pickClassName, 'h-11 flex-col rounded-[22px]', isChosen && chosenClassName)}
            >
              <span
                className={twMerge(
                  'text-xs',
                  isFull
                    ? 'text-foreground-disabled'
                    : isChosen
                      ? chosenSubtleTextClassName
                      : 'text-foreground-secondary',
                )}
              >
                {formatDay(key)}
              </span>
              <span className={twMerge('text-sm font-semibold', isFull && 'text-foreground-disabled')}>
                {formatDayOfMonth(key)}
              </span>
            </button>
          )
        })}
      </SetupCallRow>

      <SetupCallRow className="mt-4 flex min-h-6 items-center justify-between gap-3 whitespace-nowrap">
        <p className="text-sm font-medium">{day && formatDayHeading(day)}</p>
        <span className="text-foreground-secondary flex items-center gap-2 text-xs">
          <span>{timeZoneLabel(zone)}</span>
          <button
            type="button"
            className="text-[oklch(0.77_0.1404_255.5)] underline decoration-[oklch(0.534_0.1485_255.5)] underline-offset-4 transition-colors hover:text-[oklch(0.908_0.0521_255.5)] hover:decoration-[oklch(0.77_0.1404_255.5)]"
            onClick={onChangeZone}
          >
            Change
          </button>
        </span>
      </SetupCallRow>

      <SetupCallRow className="mt-2">
        {daySlots.length > 0 ? (
          <FadingScrollable minFadeSize="0px" style={{ maxHeight: slotsHeight(SLOT_MAX_ROWS) }}>
            <div
              className="grid grid-cols-3 content-start gap-1.5"
              style={{ minHeight: slotsMinHeight, gridAutoRows: SLOT_HEIGHT_PX }}
              role="group"
              aria-label="Time"
            >
              {daySlots.map((start) => (
                <button
                  key={start}
                  type="button"
                  aria-pressed={start === slot}
                  onClick={() => setSlot(start)}
                  className={twMerge(
                    pickClassName,
                    'h-7 rounded-[14px] text-sm font-medium tabular-nums',
                    start === slot && chosenClassName,
                  )}
                >
                  {formatTime(start, zone)}
                </button>
              ))}
            </div>
          </FadingScrollable>
        ) : (
          <div
            className="bg-background-subtle corner-squircle flex flex-col items-center justify-center gap-2 rounded-2xl p-3"
            style={{ minHeight: slotsMinHeight }}
          >
            <p className="text-foreground-secondary text-sm">No times left on {day && formatDay(day, 'long')}.</p>
            {nextOpenDay && (
              <Button variant="secondary" tone="on-dark" size="sm" onClick={() => setDay(nextOpenDay)}>
                See {formatDay(nextOpenDay, 'long')}
              </Button>
            )}
          </div>
        )}
      </SetupCallRow>

      <SetupCallRow className="mt-4">
        <p className={twMerge('text-sm font-medium tabular-nums', !slot && 'text-foreground-secondary')}>
          {slot ? formatSlot(slot, zone) : 'No time selected'}
        </p>
        {bookingAs.length > 0 && (
          <p className="text-foreground-secondary mt-0.5 text-xs">Booking as {bookingAs.join(' · ')}</p>
        )}
        {book.isError && <p className="text-foreground-secondary mt-0.5 text-xs">{book.error.message}</p>}
      </SetupCallRow>

      <SetupCallRow className="mt-3 flex gap-2">
        <Button size="lg" className={tactileClassName} disabled={!slot} loading={book.isPending} onClick={confirm}>
          {booking ? 'Move my call' : 'Confirm booking'}
        </Button>
        <Button variant="ghost" tone="on-dark" size="lg" className={letGoClassName} onClick={onCancel}>
          Cancel
        </Button>
      </SetupCallRow>
    </div>
  )
}
